import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { FileUp, UserPlus, Link2, RefreshCw, ExternalLink, Save, Edit3, Bell, BellOff } from "lucide-react";
import { rgApi } from "@/lib/rgApi";
import { enableAdminPush, disableAdminPush, getPushSupport, currentSubscription } from "@/lib/rgPush";
import { MESSAGES as MSG_DEFAULTS } from "./rgMessages";
import ICloudSyncBlock from "./ICloudSyncBlock";
import PrepMasterListBlock from "./PrepMasterListBlock";
import CleaningProtocolBlock from "./CleaningProtocolBlock";
import CleaningStatsBlock from "./CleaningStatsBlock";
import BreakfastConfigBlock from "./BreakfastConfigBlock";

/**
 * Settings — Réglages : import CSV Lodgify (merge/replace), ajout manuel,
 * abonnement ICS Apple/Google, dernière synchro.
 */

export default function Settings() {
  const [lastSync, setLastSync] = useState(null);
  const [importing, setImporting] = useState(null);
  const [lastResult, setLastResult] = useState(null);
  const [manualOpen, setManualOpen] = useState(false);
  const [savingManual, setSavingManual] = useState(false);
  const [m, setM] = useState({ name: "", phone: "", date_arrival: "", date_departure: "", people: 2, source: "Direct" });
  const [templates, setTemplates] = useState({ confirmation: "", jour_arrivee: "", merci: "", form_couchage: "", form_petit_dejeuner: "", form_bain_fleurs: "" });
  const [tplEditing, setTplEditing] = useState(null);
  const [tplBusy, setTplBusy] = useState(false);
  const nav = useNavigate();

  // Défauts des templates de formulaires (identiques au backend DEFAULT_TEMPLATES)
  const FORM_DEFAULTS = {
    form_couchage: "Bonjour {first_name} 🌿\nPour préparer au mieux votre arrivée, pourriez-vous m'indiquer votre choix de couchage pour le 3ᵉ voyageur via ce petit lien ?\n{lien}\nMerci beaucoup et à très vite ✨",
    form_petit_dejeuner: "Bonjour {first_name} 🌿\nAfin de préparer au mieux votre petit déjeuner, je vous invite à remplir ce petit formulaire dès que vous avez un moment 🥐\n{lien}\nMerci beaucoup et à très vite ✨",
    form_bain_fleurs: "Bonjour {first_name} 🌸\nVotre bain de fleurs sera préparé avec des fleurs fraîches soigneusement sélectionnées. Avant d'en profiter, merci de prendre connaissance de quelques précautions :\n\n🌿 À lire avant de vous baigner — évitez le bain en cas de :\n• allergie connue aux fleurs, plantes ou parfums naturels\n• peau très sensible, irritée ou présentant une lésion\n• problème dermatologique en cours\n\nNous vous conseillons également :\n• une durée de bain de 30 à 45 minutes\n• un rinçage à l'eau claire après utilisation\n\nEn cas de grossesse, d'allaitement ou de doute médical, demandez conseil à un professionnel de santé.\n\nMerci de me confirmer par retour que vous avez pris connaissance de ces précautions 🙏\nÀ très vite ✨",
  };

  const loadAll = useCallback(async () => {
    try {
      const [meta, tpl] = await Promise.all([rgApi.syncMeta(), rgApi.getTemplates()]);
      setLastSync(meta?.last_sync || null);
      setTemplates({
        confirmation: tpl?.confirmation || MSG_DEFAULTS.confirmation.body,
        jour_arrivee: tpl?.jour_arrivee || MSG_DEFAULTS.arrivee.body,
        merci: tpl?.merci || MSG_DEFAULTS.merci.body,
        form_couchage: tpl?.form_couchage || FORM_DEFAULTS.form_couchage,
        form_petit_dejeuner: tpl?.form_petit_dejeuner || FORM_DEFAULTS.form_petit_dejeuner,
        form_bain_fleurs: tpl?.form_bain_fleurs || FORM_DEFAULTS.form_bain_fleurs,
      });
    } catch (e) { if (e.status === 401) nav("/admin"); }
  }, [nav]);
  useEffect(() => { loadAll(); }, [loadAll]);

  // ============ Lodgify state ============
  const [lodgifyStatus, setLodgifyStatus] = useState(null);
  const [lodgifyBusy, setLodgifyBusy] = useState(false);
  const [lodgifyMsg, setLodgifyMsg] = useState(null);

  const loadLodgifyStatus = useCallback(async () => {
    try {
      const s = await rgApi.lodgifyStatus();
      setLodgifyStatus(s);
    } catch (e) { /* silent */ }
  }, []);
  useEffect(() => { loadLodgifyStatus(); }, [loadLodgifyStatus]);

  const testLodgify = async () => {
    setLodgifyBusy(true); setLodgifyMsg(null);
    try {
      const r = await rgApi.lodgifyTestConnection();
      setLodgifyMsg(r.ok
        ? { ok: true, text: `Connexion OK · ${r.sample_count} réservation(s) accessibles.` }
        : { ok: false, text: r.error || "Erreur inconnue" });
    } catch (e) { setLodgifyMsg({ ok: false, text: e.message }); }
    finally { setLodgifyBusy(false); }
  };
  const backfillLodgify = async () => {
    if (!window.confirm("Import initial de toutes les réservations Lodgify ?\n(non-destructif — écrase uniquement les champs Lodgify, préserve tes notes/checklists/périodes)")) return;
    setLodgifyBusy(true); setLodgifyMsg(null);
    try {
      const r = await rgApi.lodgifyBackfill();
      setLodgifyMsg({ ok: true, text: `Import : ${r.seen} vues · ${r.created} créées · ${r.updated} mises à jour.` });
      await loadLodgifyStatus();
    } catch (e) { setLodgifyMsg({ ok: false, text: e.message }); }
    finally { setLodgifyBusy(false); }
  };
  const subscribeLodgify = async () => {
    setLodgifyBusy(true); setLodgifyMsg(null);
    try {
      const target = window.location.origin; // preview or prod
      const r = await rgApi.lodgifySubscribe(target);
      const nNew = (r.webhooks || []).filter((w) => w.status === "subscribed").length;
      const nExisting = (r.webhooks || []).filter((w) => w.status === "already_exists").length;
      const nErr = (r.errors || []).length;
      let msg = "";
      if (nNew > 0) msg += `${nNew} nouveau(x) webhook(s) enregistré(s). `;
      if (nExisting > 0) msg += `${nExisting} déjà présent(s). `;
      if (nErr > 0) msg += `${nErr} erreur(s).`;
      if (!msg) msg = "Aucun changement.";
      setLodgifyMsg({ ok: r.ok, text: msg + ` Cible: ${r.target_url}` });
      await loadLodgifyStatus();
    } catch (e) { setLodgifyMsg({ ok: false, text: e.message }); }
    finally { setLodgifyBusy(false); }
  };
  const unsubscribeLodgify = async () => {
    if (!window.confirm("Désabonner tous les webhooks Lodgify ? La sync temps réel sera coupée.")) return;
    setLodgifyBusy(true); setLodgifyMsg(null);
    try {
      const r = await rgApi.lodgifyUnsubscribe();
      setLodgifyMsg({ ok: true, text: `${r.removed_ids?.length || 0} webhook(s) désabonné(s).` });
      await loadLodgifyStatus();
    } catch (e) { setLodgifyMsg({ ok: false, text: e.message }); }
    finally { setLodgifyBusy(false); }
  };
  const hardResetLodgify = async () => {
    if (!window.confirm("Hard-reset : supprimer TOUS les webhooks Lodgify ciblant cette app puis re-souscrire proprement avec un nouveau secret HMAC. À utiliser si la sync temps réel ne fonctionne pas. Continuer ?")) return;
    setLodgifyBusy(true); setLodgifyMsg(null);
    try {
      const target = window.location.origin;
      const r = await rgApi.lodgifyWebhooksHardReset(target);
      const msg = `Reset : ${r.deleted_count} supprimé(s), ${r.subscribed_count} souscrit(s). Cible : ${r.target_url}. ${r.new_secret_generated ? "Nouveau secret HMAC ✓" : "Aucun nouveau secret (Lodgify inchangé)"}. État final Lodgify : ${r.final_state_lodgify?.length || 0} webhook(s).`;
      setLodgifyMsg({ ok: r.ok, text: msg });
      await loadLodgifyStatus();
    } catch (e) { setLodgifyMsg({ ok: false, text: e.message }); }
    finally { setLodgifyBusy(false); }
  };
  const syncClosedPeriodsLodgify = async () => {
    setLodgifyBusy(true); setLodgifyMsg(null);
    try {
      const r = await rgApi.lodgifySyncClosedPeriods();
      setLodgifyMsg({
        ok: r.ok,
        text: `Fermetures Lodgify : ${r.created} nouvelle(s), ${r.skipped_duplicates} déjà présentes · ${r.properties_scanned} propriété(s) scannée(s) (${r.date_from} → ${r.date_to}).${(r.errors || []).length ? ` ${r.errors.length} erreur(s).` : ""}`,
      });
      await loadLodgifyStatus();
    } catch (e) { setLodgifyMsg({ ok: false, text: e.message }); }
    finally { setLodgifyBusy(false); }
  };

  // ============ Web Push ============
  const [pushSupport] = useState(() => getPushSupport());
  const [pushBusy, setPushBusy] = useState(false);
  const [pushMsg, setPushMsg] = useState(null);
  const [pushActive, setPushActive] = useState(false);
  const [pushStatus, setPushStatus] = useState(null);

  const loadPushState = useCallback(async () => {
    try {
      const [sub, status] = await Promise.all([
        currentSubscription(),
        rgApi.pushStatus().catch(() => null),
      ]);
      setPushActive(!!sub);
      setPushStatus(status);
    } catch { /* silent */ }
  }, []);
  useEffect(() => { loadPushState(); }, [loadPushState]);

  const enablePush = async () => {
    setPushBusy(true); setPushMsg(null);
    try {
      await enableAdminPush();
      setPushMsg({ ok: true, text: "Notifications push activées." });
      await loadPushState();
    } catch (e) { setPushMsg({ ok: false, text: e.message }); }
    finally { setPushBusy(false); }
  };
  const disablePush = async () => {
    if (!window.confirm("Désactiver les notifications push sur cet appareil ?")) return;
    setPushBusy(true); setPushMsg(null);
    try {
      await disableAdminPush();
      setPushMsg({ ok: true, text: "Notifications désactivées." });
      await loadPushState();
    } catch (e) { setPushMsg({ ok: false, text: e.message }); }
    finally { setPushBusy(false); }
  };
  const testPush = async () => {
    setPushBusy(true); setPushMsg(null);
    try {
      const r = await rgApi.pushTest();
      setPushMsg({ ok: true, text: `Notification test envoyée à ${r.sent}/${r.total} abonné(s).` });
    } catch (e) { setPushMsg({ ok: false, text: e.message }); }
    finally { setPushBusy(false); }
  };


  const saveTemplate = async (key) => {
    setTplBusy(true);
    try {
      await rgApi.putTemplates(templates);
      setTplEditing(null);
    } catch (e) { alert("Erreur : " + e.message); }
    finally { setTplBusy(false); }
  };
  const resetTemplate = (key) => {
    const defaults = { confirmation: MSG_DEFAULTS.confirmation.body, jour_arrivee: MSG_DEFAULTS.arrivee.body, merci: MSG_DEFAULTS.merci.body, ...FORM_DEFAULTS };
    setTemplates({ ...templates, [key]: defaults[key] });
  };

  const pickCsv = (mode) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".csv,text/csv";
    input.onchange = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      const message = mode === "replace"
        ? "L'import REMPLACE TOUT :\n• Toutes les réservations actuelles seront supprimées\n• Les statuts « Fait » seront remis à zéro\n\nContinuer ?"
        : "L'import est NON destructif :\n• Les nouvelles réservations sont ajoutées\n• Les existantes sont mises à jour (dates, nom, téléphone)\n• Les statuts « Fait », notes, conversations sont conservés\n\nContinuer ?";
      if (!window.confirm(message)) return;
      setImporting(mode); setLastResult(null);
      try {
        const res = await rgApi.importCsv(file, file.name, mode);
        setLastResult({ ...res, _mode: mode });
        await loadAll();
      } catch (err) { alert("Erreur import : " + err.message); }
      finally { setImporting(null); }
    };
    input.click();
  };

  const submitManual = async () => {
    if (!m.name || !m.date_arrival || !m.date_departure) { alert("Nom + dates requis"); return; }
    setSavingManual(true);
    try {
      await rgApi.createReservation({ ...m, people: Number(m.people) || 2 });
      setManualOpen(false);
      setM({ name: "", phone: "", date_arrival: "", date_departure: "", people: 2, source: "Direct" });
      alert("Réservation créée !");
    } catch (e) { alert("Erreur : " + e.message); }
    finally { setSavingManual(false); }
  };

  const icsUrl = rgApi.icsExportUrl();
  const copyIcs = async () => {
    try { await navigator.clipboard.writeText(icsUrl); alert("Lien ICS copié — abonnez-vous dans Apple Calendar / Google Calendar."); }
    catch { window.prompt("Copiez ce lien ICS :", icsUrl); }
  };

  const [bfSyncing, setBfSyncing] = useState(false);
  const [bfSyncResult, setBfSyncResult] = useState(null);
  const syncBreakfast = async () => {
    setBfSyncing(true); setBfSyncResult(null);
    try {
      const res = await rgApi.syncBreakfast();
      // Backend renvoie {ok:true/false, ...} (pas d'exception sur erreur métier)
      setBfSyncResult(res);
    } catch (e) {
      setBfSyncResult({ ok: false, error: e.message });
    } finally { setBfSyncing(false); }
  };
  const pickBreakfastCsv = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".csv,text/csv";
    input.onchange = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      setBfSyncing(true); setBfSyncResult(null);
      try {
        const res = await rgApi.importBreakfastCsv(file);
        setBfSyncResult({ ok: true, ...res });
      } catch (err) {
        setBfSyncResult({ ok: false, error: err.message });
      } finally { setBfSyncing(false); }
    };
    input.click();
  };

  return (
    <div className="rg-page" data-testid="rg-settings-page">
      <h2 className="rg-h2">Réglages</h2>
      <p className="rg-muted" style={{ marginBottom: 16 }}>
        Dernière synchro : {lastSync ? new Date(lastSync).toLocaleString("fr-FR") : "jamais"}
      </p>

      {/* Lodgify Live Sync (temps réel) */}
      <div className="rg-card" style={{ marginBottom: 16, borderLeft: "3px solid #10b981" }} data-testid="rg-lodgify-section">
        <h3 className="rg-h3">🔗 Lodgify — Sync temps réel</h3>
        <p className="rg-muted" style={{ marginBottom: 10, fontSize: 12 }}>
          Réservations et messages voyageurs importés automatiquement depuis Lodgify.
          <b> Lecture seule</b> — rien n&apos;est jamais renvoyé vers Lodgify.
        </p>
        {lodgifyStatus && (
          <div className="rg-muted" style={{ fontSize: 12, marginBottom: 10, padding: "8px 10px", background: "var(--rg-border-soft)", borderRadius: 6 }} data-testid="rg-lodgify-status">
            <div>Clé API : {lodgifyStatus.api_key_present
              ? <span style={{ color: "var(--rg-success)" }}>✓ Chargée ({lodgifyStatus.api_key_masked})</span>
              : <span style={{ color: "#dc2626" }}>⚠ Manquante dans .env</span>}</div>
            <div>
              Enregistrés côté serveur : <b>{lodgifyStatus.webhooks_registered?.length || 0}</b>
              {" · "}
              <span title="Source de vérité : appelle GET /webhooks/v1/list en direct">
                Réels chez Lodgify : <b>{lodgifyStatus.webhooks_remote_count || 0}</b>
              </span>
            </div>
            {lodgifyStatus.webhooks_remote_error && (
              <div style={{ color: "#dc2626" }}>⚠ Erreur listing distant : {lodgifyStatus.webhooks_remote_error}</div>
            )}
            {(lodgifyStatus.webhooks_remote_lodgify || []).map((w, i) => (
              <div key={i} style={{ fontSize: 10, marginLeft: 8, color: "var(--rg-text-3)" }}>
                • {w.event} → <code>{w.url}</code>
              </div>
            ))}
            {lodgifyStatus.last_backfill && (
              <div>Dernier import : {new Date(lodgifyStatus.last_backfill).toLocaleString("fr-FR")}</div>
            )}
            {lodgifyStatus.last_webhook_at ? (
              <div>Dernier webhook <b>réellement reçu</b> : {new Date(lodgifyStatus.last_webhook_at).toLocaleString("fr-FR")} <span style={{ color: "var(--rg-text-3)" }}>({lodgifyStatus.last_webhook_event || "?"})</span></div>
            ) : (
              <div style={{ color: "#dc2626" }}>⚠ Aucun webhook reçu à ce jour</div>
            )}
            {lodgifyStatus.last_webhook_error && (
              <div style={{ color: "#dc2626" }}>Dernière erreur webhook : {lodgifyStatus.last_webhook_error.reason} ({lodgifyStatus.last_webhook_error.status_code}) — {new Date(lodgifyStatus.last_webhook_error.at).toLocaleString("fr-FR")}</div>
            )}
            {lodgifyStatus.hard_reset_at && (
              <div style={{ color: "var(--rg-text-3)" }}>Dernier hard-reset : {new Date(lodgifyStatus.hard_reset_at).toLocaleString("fr-FR")}</div>
            )}
            {lodgifyStatus.counters && Object.keys(lodgifyStatus.counters).length > 0 && (
              <div>Compteurs : {Object.entries(lodgifyStatus.counters).map(([k, v]) => `${k}: ${v}`).join(" · ")}</div>
            )}
            {(lodgifyStatus.seen_statuses || []).length > 0 && (
              <div style={{ color: "var(--rg-text-3)" }}>Statuts vus Lodgify : {(lodgifyStatus.seen_statuses || []).join(" · ")}</div>
            )}
          </div>
        )}
        <div className="rg-row" style={{ gap: 8, flexWrap: "wrap" }}>
          <button className="rg-btn ghost small" onClick={testLodgify} disabled={lodgifyBusy} data-testid="rg-lodgify-test">
            <Link2 size={13} /> Tester connexion
          </button>
          <button className="rg-btn" onClick={backfillLodgify} disabled={lodgifyBusy} data-testid="rg-lodgify-backfill">
            <RefreshCw size={13} /> {lodgifyBusy ? "Sync…" : "Import initial (backfill)"}
          </button>
          <button className="rg-btn ghost" onClick={syncClosedPeriodsLodgify} disabled={lodgifyBusy} data-testid="rg-lodgify-sync-closed">
            🔒 Importer fermetures Lodgify
          </button>
          <button
            className="rg-btn"
            onClick={hardResetLodgify}
            disabled={lodgifyBusy}
            data-testid="rg-lodgify-hard-reset"
            style={{ background: "#dc2626", color: "#fff" }}
            title="Nettoyage complet : supprime tous les webhooks Lodgify de cette app puis re-souscrit proprement"
          >
            🔧 Hard-reset webhooks
          </button>
          {lodgifyStatus?.webhooks_registered?.length > 0 && (
            <button className="rg-btn ghost small" onClick={unsubscribeLodgify} disabled={lodgifyBusy} data-testid="rg-lodgify-unsubscribe" style={{ color: "#dc2626" }}>
              Désabonner webhooks
            </button>
          )}
          {lodgifyStatus && (lodgifyStatus.webhooks_registered?.length === 0) && (
            <button className="rg-btn" onClick={subscribeLodgify} disabled={lodgifyBusy} data-testid="rg-lodgify-subscribe">
              ⚡ Souscrire aux webhooks (temps réel)
            </button>
          )}
        </div>
        {lodgifyMsg && (
          <div className={`rg-muted ${lodgifyMsg.ok ? "" : "rg-alert-warn"}`} style={{ marginTop: 10, fontSize: 12, padding: lodgifyMsg.ok ? 0 : "10px" }} data-testid="rg-lodgify-msg">
            {lodgifyMsg.ok ? <>✓ {lodgifyMsg.text}</> : <><b>Erreur :</b> {lodgifyMsg.text}</>}
          </div>
        )}
      </div>

      {/* Notifications push admin */}
      <div className="rg-card" style={{ marginBottom: 16, borderLeft: "3px solid #6366f1" }} data-testid="rg-push-section">
        <h3 className="rg-h3">🔔 Notifications push admin</h3>
        <p className="rg-muted" style={{ marginBottom: 10, fontSize: 12 }}>
          Reçois un bip web sur cet appareil : <b>nouvelle réservation</b> ou <b>message voyageur</b> (Lodgify), <b>petit-déjeuner validé</b> par un voyageur, et le <b>rappel d&apos;arrivée à 14 h</b> (heure de La Réunion).
          {" "}Chaque appareil doit être activé séparément.
        </p>
        {!pushSupport.supported && (
          <div className="rg-alert-warn" style={{ marginBottom: 10 }} data-testid="rg-push-unsupported">
            Ce navigateur ne supporte pas les notifications push
            {typeof navigator !== "undefined" && /iPhone|iPad|iPod/.test(navigator.userAgent) ? " (iOS : ajoutez d'abord le site à l'écran d'accueil)" : ""}.
          </div>
        )}
        {pushSupport.supported && (
          <>
            <div className="rg-muted" style={{ fontSize: 12, marginBottom: 10, padding: "8px 10px", background: "var(--rg-border-soft)", borderRadius: 6 }} data-testid="rg-push-status">
              <div>État sur cet appareil : {pushActive
                ? <span style={{ color: "var(--rg-success)" }}>✓ Actives</span>
                : <span style={{ color: "#6b7280" }}>Désactivées</span>}</div>
              <div>Permission navigateur : <b>{pushSupport.permission}</b></div>
              {pushStatus && (
                <>
                  <div>Abonnés (tous appareils) : <b>{pushStatus.subscribers_count}</b></div>
                  <div>Clé VAPID serveur : {pushStatus.vapid_public_key_present
                    ? <span style={{ color: "var(--rg-success)" }}>✓ Chargée</span>
                    : <span style={{ color: "#dc2626" }}>⚠ Manquante</span>}</div>
                </>
              )}
            </div>
            <div className="rg-row" style={{ gap: 8, flexWrap: "wrap" }}>
              {!pushActive ? (
                <button className="rg-btn" onClick={enablePush} disabled={pushBusy} data-testid="rg-push-enable">
                  <Bell size={13} /> {pushBusy ? "Activation…" : "Activer sur cet appareil"}
                </button>
              ) : (
                <>
                  <button className="rg-btn ghost small" onClick={testPush} disabled={pushBusy} data-testid="rg-push-test">
                    <Bell size={13} /> Envoyer test
                  </button>
                  <button className="rg-btn ghost small" onClick={disablePush} disabled={pushBusy} data-testid="rg-push-disable" style={{ color: "#dc2626" }}>
                    <BellOff size={13} /> Désactiver
                  </button>
                </>
              )}
            </div>
            {pushMsg && (
              <div className={`rg-muted ${pushMsg.ok ? "" : "rg-alert-warn"}`} style={{ marginTop: 10, fontSize: 12, padding: pushMsg.ok ? 0 : "10px" }} data-testid="rg-push-msg">
                {pushMsg.ok ? <>✓ {pushMsg.text}</> : <><b>Erreur :</b> {pushMsg.text}</>}
              </div>
            )}
          </>
        )}
      </div>

      {/* CSV Import */}
      <div className="rg-card" style={{ marginBottom: 16 }}>
        <h3 className="rg-h3">Import CSV Lodgify</h3>
        <p className="rg-muted" style={{ marginBottom: 10 }}>Fallback si la sync temps réel est coupée. Utilisez plutôt la sync ci-dessus.</p>
        <div className="rg-row" style={{ gap: 8, flexWrap: "wrap" }}>
          <button className="rg-btn" onClick={() => pickCsv("merge")} disabled={importing !== null} data-testid="rg-import-merge">
            <FileUp size={13} /> {importing === "merge" ? "Import…" : "Merge (ajout / mise à jour)"}
          </button>
          <button className="rg-btn danger" onClick={() => pickCsv("replace")} disabled={importing !== null} data-testid="rg-import-replace">
            <RefreshCw size={13} /> {importing === "replace" ? "Import…" : "Replace (remplacement complet)"}
          </button>
        </div>
        {lastResult && (
          <div className="rg-muted" style={{ marginTop: 12, fontSize: 12 }} data-testid="rg-import-result">
            <b>Mode :</b> {lastResult._mode} · <b>Créées :</b> {lastResult.created ?? 0} · <b>Mises à jour :</b> {lastResult.updated ?? 0}
          </div>
        )}
      </div>

      {/* Ajout manuel */}
      <div className="rg-card" style={{ marginBottom: 16 }}>
        <h3 className="rg-h3">Ajout manuel</h3>
        <p className="rg-muted" style={{ marginBottom: 10 }}>Créer une réservation directe (hors Lodgify).</p>
        {!manualOpen ? (
          <button className="rg-btn ghost" onClick={() => setManualOpen(true)} data-testid="rg-manual-open">
            <UserPlus size={13} /> Nouvelle réservation
          </button>
        ) : (
          <div className="rg-grid-2" data-testid="rg-manual-form">
            <label>Nom<input className="rg-input" value={m.name} onChange={(e) => setM({...m, name: e.target.value})} /></label>
            <label>Téléphone<input className="rg-input" value={m.phone} onChange={(e) => setM({...m, phone: e.target.value})} placeholder="+262..." /></label>
            <label>Arrivée<input className="rg-input" type="date" value={m.date_arrival} onChange={(e) => setM({...m, date_arrival: e.target.value})} /></label>
            <label>Départ<input className="rg-input" type="date" value={m.date_departure} onChange={(e) => setM({...m, date_departure: e.target.value})} /></label>
            <label>Personnes<input className="rg-input" type="number" min="1" max="6" value={m.people} onChange={(e) => setM({...m, people: e.target.value})} /></label>
            <label>Source<select className="rg-select" value={m.source} onChange={(e) => setM({...m, source: e.target.value})}>
              <option>Direct</option><option>Booking.com</option><option>Airbnb</option><option>WhatsApp</option><option>Autre</option>
            </select></label>
            <div className="rg-row" style={{ gridColumn: "1 / -1", gap: 8, justifyContent: "flex-end" }}>
              <button className="rg-btn ghost" onClick={() => setManualOpen(false)} disabled={savingManual}>Annuler</button>
              <button className="rg-btn" onClick={submitManual} disabled={savingManual} data-testid="rg-manual-save">{savingManual ? "…" : "Créer"}</button>
            </div>
          </div>
        )}
      </div>

      {/* Calendrier ICS */}
      <div className="rg-card" style={{ marginBottom: 16 }}>
        <h3 className="rg-h3">Abonnement calendrier</h3>
        <p className="rg-muted" style={{ marginBottom: 10 }}>Copiez le lien ICS pour vous abonner depuis Apple Calendar, Google Calendar ou Outlook.</p>
        <div className="rg-row" style={{ gap: 8, flexWrap: "wrap" }}>
          <button className="rg-btn" onClick={copyIcs} data-testid="rg-ics-copy"><Link2 size={13} /> Copier le lien ICS</button>
          <a href={icsUrl} target="_blank" rel="noopener noreferrer" className="rg-btn ghost" data-testid="rg-ics-open"><ExternalLink size={13} /> Ouvrir le flux</a>
        </div>
      </div>

      {/* Sync formulaire petit-déjeuner (Google Sheet) */}
      <div className="rg-card" style={{ marginBottom: 16 }} data-testid="rg-breakfast-sync-section">
        <h3 className="rg-h3">🥐 Reprise des réponses Google Forms (petit-déjeuner)</h3>
        <p className="rg-muted" style={{ marginBottom: 10 }}>
          Rapatrie en une fois les réponses déjà reçues via l&apos;ancien Google Forms (sans redemander aux voyageurs).
          Requiert que le sheet soit partagé en <b>« Anyone with the link · Viewer »</b>.
          Sinon, exportez-le en CSV et utilisez l&apos;import manuel. Les nouveaux formulaires utilisent désormais le lien individuel natif.
        </p>
        <div className="rg-row" style={{ gap: 8, flexWrap: "wrap" }}>
          <button className="rg-btn" onClick={syncBreakfast} disabled={bfSyncing} data-testid="rg-bf-sync">
            <RefreshCw size={13} /> {bfSyncing ? "Synchro…" : "Synchroniser depuis Google Sheet"}
          </button>
          <button className="rg-btn ghost" onClick={pickBreakfastCsv} disabled={bfSyncing} data-testid="rg-bf-csv">
            <FileUp size={13} /> Import CSV manuel
          </button>
        </div>
        {bfSyncResult && (
          <div className={`rg-muted ${bfSyncResult.ok ? "" : "rg-alert-warn"}`} style={{ marginTop: 10, fontSize: 12, padding: bfSyncResult.ok ? 0 : "10px" }} data-testid="rg-bf-result">
            {bfSyncResult.ok ? (
              <>
                <b>{bfSyncResult.matched || 0}</b> voyageur(s) mis à jour · <b>{bfSyncResult.unmatched || 0}</b> non trouvé(s) · <b>{bfSyncResult.total_rows || 0}</b> ligne(s) lue(s).
                {bfSyncResult.unmatched_names?.length > 0 && (
                  <div style={{ marginTop: 4 }}>Non matchés : {bfSyncResult.unmatched_names.join(", ")}</div>
                )}
              </>
            ) : (
              <><b>Sync impossible :</b> {bfSyncResult.error}</>
            )}
          </div>
        )}
      </div>

      <BreakfastConfigBlock />

      {/* Templates messages */}
      <div className="rg-card" style={{ marginBottom: 16 }} data-testid="rg-templates-section">
        <h3 className="rg-h3">💬 Templates des messages</h3>
        <p className="rg-muted" style={{ marginBottom: 10 }}>
          Modifiez le contenu des messages copiables. Variables : <code>{"{name}"}</code>, <code>{"{first_name}"}</code>, <code>{"{date_arrival}"}</code>, <code>{"{date_departure}"}</code>, <code>{"{lien}"}</code> (formulaires).
          <br />
          <span style={{ color: "var(--rg-text-3)", fontSize: 11 }}>
            Les modèles de formulaires (couchage · petit-déj · bain de fleurs) s&apos;affichent dans la fiche voyageur, dans le bloc dédié de chaque expérience, où ils restent aussi modifiables avant chaque envoi.
          </span>
        </p>
        {[
          { key: "confirmation", label: "Confirmation" },
          { key: "jour_arrivee", label: "Jour J · code arrivée" },
          { key: "merci", label: "Merci · avis" },
          { key: "form_couchage", label: "Formulaire couchage (3e voyageur)" },
          { key: "form_petit_dejeuner", label: "Formulaire petit-déjeuner" },
          { key: "form_bain_fleurs", label: "Précautions bain de fleurs" },
        ].map(({ key, label }) => (
          <div key={key} className="rg-card" style={{ marginBottom: 10, background: "var(--rg-bg)" }}>
            <div className="rg-row" style={{ justifyContent: "space-between", marginBottom: 6 }}>
              <p className="rg-tiny" style={{ margin: 0 }}>{label}</p>
              {tplEditing === key ? (
                <div className="rg-row" style={{ gap: 6 }}>
                  <button className="rg-btn ghost small" onClick={() => resetTemplate(key)}>Défaut</button>
                  <button className="rg-btn small" onClick={() => saveTemplate(key)} disabled={tplBusy} data-testid={`rg-tpl-save-${key}`}><Save size={12} /> Enregistrer</button>
                  <button className="rg-btn ghost small" onClick={() => setTplEditing(null)}>Fermer</button>
                </div>
              ) : (
                <button className="rg-btn ghost small" onClick={() => setTplEditing(key)} data-testid={`rg-tpl-edit-${key}`}><Edit3 size={12} /> Modifier</button>
              )}
            </div>
            {tplEditing === key ? (
              <textarea className="rg-textarea" style={{ minHeight: 160 }}
                value={templates[key]}
                onChange={(e) => setTemplates({ ...templates, [key]: e.target.value })}
                data-testid={`rg-tpl-input-${key}`} />
            ) : (
              <p style={{ margin: 0, whiteSpace: "pre-wrap", fontSize: 12, lineHeight: 1.5, color: "var(--rg-text-2)" }}>
                {templates[key]}
              </p>
            )}
          </div>
        ))}
      </div>

      {/* Parcours voyageur (déplacé depuis la fiche voyageur — à revoir) */}
      <div className="rg-card" style={{ marginBottom: 16, borderLeft: "3px solid #f6c343" }} data-testid="rg-parcours-section">
        <h3 className="rg-h3">🗺️ Parcours voyageur</h3>
        <div className="rg-alert-warn" style={{ marginBottom: 12 }}>
          <div className="rg-alert-title">⚙️ Section à revoir</div>
          <p className="rg-muted" style={{ margin: "6px 0 0", fontSize: 12 }}>
            Ce parcours a été déplacé depuis la fiche voyageur. Certaines étapes seront prochainement
            envoyées automatiquement — plus besoin de les cocher manuellement.
            <br />En attendant, la checklist reste éditable directement sur chaque fiche voyageur
            (ancien comportement conservé côté base).
          </p>
        </div>
        <ul style={{ margin: 0, paddingLeft: 20, color: "var(--rg-text-2)", fontSize: 13, lineHeight: 1.7 }}>
          <li>Contact créé</li>
          <li>Message de confirmation</li>
          <li>Message jour d&apos;arrivée</li>
          <li>Message de merci / avis</li>
        </ul>
      </div>

      {/* Template : Choix couchage 3e voyageur — Mode TEST */}
      <CouchageTestBlock />

      {/* Calendrier iCloud personnel — CalDAV lecture seule */}
      <ICloudSyncBlock />

      {/* Liste maîtresse "Préparation jour J" — éditable + imprimable A4 */}
      <PrepMasterListBlock />

      {/* Configuration du Cockpit ménage — blocs et actions modifiables */}
      <CleaningProtocolBlock />

      <CleaningStatsBlock />
    </div>
  );
}

function CouchageTestBlock() {
  const PUBLIC_URL = `${window.location.origin}/formulaire-couchage`;

  const [phone, setPhone] = useState("");
  const [initialPhone, setInitialPhone] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const p = await rgApi.getCouchageAdminPhone();
        setPhone(p || ""); setInitialPhone(p || "");
      } catch { /* ignore */ }
      finally { setLoading(false); }
    })();
  }, []);

  const save = async () => {
    setBusy(true); setSaved(false);
    try {
      await rgApi.setCouchageAdminPhone(phone.trim());
      setInitialPhone(phone.trim());
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      alert("Erreur : " + e.message);
    } finally { setBusy(false); }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(PUBLIC_URL);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { window.prompt("Copiez le lien :", PUBLIC_URL); }
  };

  const dirty = phone.trim() !== initialPhone.trim();

  return (
    <div className="rg-card" style={{ marginBottom: 16, borderLeft: "3px solid #7c9268" }} data-testid="rg-couchage-template-section">
      <h3 className="rg-h3">🛏️ Choix couchage — 3<sup>e</sup> voyageur enfant</h3>
      <p className="rg-muted" style={{ marginBottom: 12, fontSize: 13 }}>
        Un seul lien à copier-coller pour tous les voyageurs. Ils choisissent visuellement leur couchage
        (<b>lit parapluie</b> ou <b>canapé dépliant</b>) puis un bouton renvoie automatiquement le résumé
        dans ta conversation WhatsApp avec eux.
      </p>

      {/* Config numéro admin */}
      <div style={{ background: "var(--rg-bg)", borderRadius: 8, padding: 12, marginBottom: 12 }}>
        <p className="rg-tiny" style={{ margin: "0 0 6px", fontWeight: 600, color: "var(--rg-text-2)" }}>
          Ton numéro WhatsApp (destinataire du résumé)
        </p>
        <div className="rg-row" style={{ gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          <input
            type="tel"
            className="rg-input"
            placeholder="Ex. +262692000000 (avec indicatif pays)"
            value={loading ? "…" : phone}
            onChange={(e) => setPhone(e.target.value)}
            disabled={loading || busy}
            style={{ flex: 1, minWidth: 220 }}
            data-testid="rg-couchage-admin-phone"
          />
          <button
            type="button"
            className="rg-btn small"
            onClick={save}
            disabled={loading || busy || !dirty}
            data-testid="rg-couchage-admin-phone-save"
          >
            💾 Enregistrer
          </button>
        </div>
        {saved && <p className="rg-tiny" style={{ margin: "6px 0 0", color: "#3d5028" }}>✅ Numéro enregistré.</p>}
      </div>

      {/* Lien unique à partager */}
      <div style={{ background: "#fff8e1", border: "1px dashed #f6c343", borderRadius: 8, padding: 12 }}>
        <p style={{ margin: 0, fontWeight: 600, fontSize: 13, color: "#5a4a12" }}>🔗 Le lien à envoyer aux voyageurs</p>
        <p className="rg-tiny" style={{ margin: "6px 0 10px", color: "#5a4a12" }}>
          Toujours le même — copie-colle-le dans ta conversation WhatsApp avec un voyageur (ou dans un template).
        </p>
        <div style={{ background: "#fff", padding: 10, borderRadius: 6, fontFamily: "monospace", fontSize: 13, wordBreak: "break-all" }}>
          {PUBLIC_URL}
        </div>
        <div className="rg-row" style={{ gap: 8, marginTop: 10, flexWrap: "wrap" }}>
          <button
            type="button"
            className="rg-btn small"
            onClick={copyLink}
            data-testid="rg-couchage-copy-link"
          >
            {copied ? "✅ Copié !" : "📋 Copier le lien"}
          </button>
          <a
            href={PUBLIC_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="rg-btn ghost small"
            style={{ textDecoration: "none" }}
            data-testid="rg-couchage-preview"
          >
            👁 Prévisualiser le formulaire
          </a>
        </div>
      </div>

      <div style={{ marginTop: 12, background: "var(--rg-bg)", borderRadius: 8, padding: 12, fontSize: 12, color: "var(--rg-text-2)" }}>
        <p style={{ margin: 0, fontWeight: 600 }}>Comment ça marche ?</p>
        <ol style={{ margin: "8px 0 0", paddingLeft: 18, lineHeight: 1.7 }}>
          <li>Le voyageur ouvre le lien sur son téléphone.</li>
          <li>Il choisit son couchage (lit parapluie ou canapé dépliant).</li>
          <li>Un bouton vert « Envoyer à mon hôte via WhatsApp » apparaît.</li>
          <li>En tapant dessus, WhatsApp s&apos;ouvre avec ton numéro et un résumé pré-rédigé.</li>
          <li>Il tape « Envoyer » → le résumé arrive dans ta conversation WhatsApp avec lui.</li>
        </ol>
      </div>
    </div>
  );
}
